import React from 'react';
import { AdacButton, AdacText } from '@adac-de/component-library';
import styles from './select-button.module.scss';
import { getIcon, isIconType } from '@components/icons/icons';

type SelectButtonProps<T> = {
  isSelected: boolean;
  title: string;
  value: T;
  icon?: string;
  subline?: string;
  handleOnClick: (selectedValue: T) => void;
  ariaLabel: string;
} & React.HtmlHTMLAttributes<HTMLButtonElement>;

export const SelectButton = <T extends string>({
  value,
  isSelected,
  handleOnClick,
  title,
  icon,
  subline,
  ariaLabel,
  ...rest
}: SelectButtonProps<T>) => {
  const IconComponent = isIconType(icon) && getIcon(icon);
  return (
    <AdacButton
      variant="secondary"
      onClick={() => handleOnClick(value)}
      className={isSelected ? [styles.button, styles.buttonActive].join(' ') : styles.button}
      aria-describedby={ariaLabel}
      {...rest}
    >
      {isSelected && <span className="screen-reader-only">Ausgewählt: </span>}
      {IconComponent && <IconComponent className={styles.icon} />}
      <AdacText type="small" tagName="span">
        {title}
      </AdacText>
      {!icon && subline && (
        <AdacText type="extra-small" color="secondary" tagName="span">
          {subline}
        </AdacText>
      )}
    </AdacButton>
  );
};

export const SelectButtonContainer: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  children
}) => {
  const numberOfButtons = (children as React.ReactNode[]).length;
  const buttonsToColumnClassMap: Record<number, string> = {
    2: styles.container2Col,
    3: styles.container3Col,
    4: styles.container2Col,
    5: styles.container3Col,
    6: styles.container3Col,
    7: styles.container4Col,
    8: styles.container4Col
  };

  return (
    <div className={[styles.container, buttonsToColumnClassMap[numberOfButtons]].join(' ')}>
      {children}
    </div>
  );
};
